import { useEffect, useMemo, useState } from 'react';
import {
  AlertMessage,
  IconStyle,
  PromotionalMessageType,
  Theme,
  useTranslation,
  IconAlignment,
} from '@nextgen-web-framework/all';
import { EligibleCoupon } from '@commerce-promotion-webcomponents/sdk';
import { CouponsProps, CouponItem } from './types/propTypes';
import CouponsDrawer from './CouponsDrawer/CouponsDrawer';
import styles from './Coupons.module.scss';

const Coupons = ({
  applicableCoupons,
  potentialCoupons,
  appliedCoupons,
  firedPromotions,
  showDrawerOnly = false,
  applyCoupon,
  onDrawerClose,
}: CouponsProps) => {
  const [showDrawer, setShowDrawer] = useState<boolean>(showDrawerOnly);
  const [appliedCouponsList, setAppliedCouponsList] = useState<CouponItem[]>([]);
  const [couponsList, setCouponsList] = useState<CouponItem[]>([]);
  const t = useTranslation();
  const availableCouponsText = t('promotion.coupons.couponsAvailableForOrder');
  const appliedCouponsText = t('promotion.coupons.appliedCoupons');
  const addCouponText = t('promotion.coupons.addCoupon');
  const hasCoupons = couponsList.length > 0 || appliedCouponsList.length > 0;

  const alertText = useMemo(
    () =>
      hasCoupons
        ? appliedCouponsList.length > 0
          ? appliedCouponsText
              .replace('{{count}}', `${appliedCouponsList.length}`)
              .replace('{{total}}', `${appliedCouponsList.length + couponsList.length}`)
          : availableCouponsText.replace('{{count}}', `${couponsList.length}`)
        : addCouponText,
    [couponsList, appliedCouponsList],
  );

  useEffect(() => {
    const appliedList: CouponItem[] = [];
    let applicableList = getSortedCouponsList(
      transformCouponsList(applicableCoupons, PromotionalMessageType.POTENTIAL),
    );
    applicableList = applicableList.filter((item) => {
      if (appliedCoupons?.includes(item.couponCode)) {
        appliedList.push({ ...item, couponType: PromotionalMessageType.APPLIED });
        return false;
      }
      return true;
    });
    // If some applied coupons not found in applicableCoupons, find them in firedPromotions
    if (appliedCoupons?.length != appliedList.length) {
      const list = (firedPromotions ?? [])
        .filter(
          ({ promotion }) =>
            promotion?.coupon &&
            appliedCoupons?.includes(promotion?.coupon) &&
            !appliedList.find((ob) => ob.couponCode === promotion.coupon),
        )
        .map(({ promotion }) => ({
          name: promotion?.name,
          description: promotion?.message,
          couponEndDate: promotion?.endDate, // This will come from couponEndDate in future
          couponCode: promotion?.coupon as string,
          couponType: PromotionalMessageType.APPLIED,
        }));
      appliedList.push(...list);
    }

    const potentialList = getSortedCouponsList(transformCouponsList(potentialCoupons, PromotionalMessageType.EXPIRED));
    setCouponsList([...applicableList, ...potentialList]);
    setAppliedCouponsList(getSortedCouponsList(appliedList));
  }, [applicableCoupons, potentialCoupons, appliedCoupons, firedPromotions]);

  const transformCouponsList = (list: EligibleCoupon[] = [], couponType: PromotionalMessageType) =>
    list?.reduce((result: CouponItem[], item) => {
      if (item.coupon) {
        result.push({
          ...item,
          couponType,
          couponCode: item.coupon,
          name: item.title?.[0]?.text,
          description: item.message?.[0]?.text,
        });
      }
      return result;
    }, []);

  const getSortedCouponsList = (list: CouponItem[]) => {
    const sortedList = list.sort((c1, c2) => {
      const getTime = (date?: Date | string) => new Date(date as Date).getTime();
      const getTitle = (coupon: CouponItem) => String(coupon.name).toLowerCase();
      const timeDiff = getTime(c1.couponEndDate) - getTime(c2.couponEndDate);
      if (timeDiff !== 0) {
        return timeDiff;
      }
      return getTitle(c1) > getTitle(c2) ? 1 : -1;
    });

    return sortedList;
  };

  const handleDrawerClose = () => {
    setShowDrawer(false);
    onDrawerClose?.();
  };

  return (
    <div>
      {!showDrawerOnly && (
        <AlertMessage
          className={styles['coupon-alert-message']}
          theme={hasCoupons ? (appliedCouponsList.length > 0 ? Theme.SUCCESS : Theme.ACTION) : Theme.GRAY}
          icon="ticket"
          iconStyle={IconStyle.SOLID}
          iconAlignment={IconAlignment.CENTER}
          onClick={() => setShowDrawer(true)}
        >
          <span>{alertText}</span>
        </AlertMessage>
      )}
      {showDrawer && (
        <CouponsDrawer
          couponsList={couponsList}
          appliedCouponsList={appliedCouponsList}
          appliedCoupons={appliedCoupons}
          applyCoupon={applyCoupon}
          closeDrawer={handleDrawerClose}
        />
      )}
    </div>
  );
};

export default Coupons;
